import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { libraryApi, productsApi } from "@/lib/api";
import { openWixStore } from "@/lib/wixLinks";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { Lock, Check, ArrowRight } from "lucide-react";

const COVERS = {
  quiz: { src: "/images/quiz-cover.jpg", fit: "contain" },
  starter_kit: { src: "/images/starter-kit-cover.jpg", fit: "cover" },
  guidebook: { src: "/images/guidebook-cover.jpg", fit: "cover" },
  manual: { src: "/images/manual-cover.png", fit: "cover" },
};

export default function Library() {
  const nav = useNavigate();
  const [shelves, setShelves] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([libraryApi.getLibrary(), productsApi.getMyUnlocks()])
      .then(([lib, unl]) => {
        setShelves(lib.shelves || []);
        setProducts(unl.products || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const bundle = products.find((p) => p.product_id === "bundle_complete");

  return (
    <div className="space-y-8" data-testid={IDS.libraryPage}>
      <header>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Library</h1>
        <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>
          Your guides, manuals, and companion materials.
        </p>
      </header>

      {shelves.map((shelf) => (
        <section key={shelf.key} className="space-y-3">
          <h2 className="pa-serif text-xl" style={{ color: "var(--pa-gold)" }}>{shelf.title}</h2>
          {shelf.coming_soon && (
            <p className="text-xs italic" style={{ color: "var(--pa-text-muted)" }}>More content coming soon.</p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {(shelf.items || []).map((it) => {
              const cover = COVERS[it.product_id];
              return (
                <button
                  key={it.item_id}
                  onClick={() => {
                    if (it.locked) openWixStore();
                    else if (it.product_id === "manual") nav("/manual");
                    else nav(`/reader/${it.item_id}`);
                  }}
                  className="pa-card p-4 flex items-center gap-4 text-left transition-colors hover:border-[color:var(--pa-gold-dark)]"
                  data-testid={IDS.libraryItem(it.item_id)}
                >
                  {cover && (
                    <div
                      className="flex-shrink-0 rounded overflow-hidden"
                      style={{
                        width: 60,
                        height: 82,
                        background: "var(--pa-input-bg)",
                        border: "1px solid var(--pa-card-border)",
                      }}
                    >
                      <img
                        src={cover.src}
                        alt={it.title}
                        className="w-full h-full"
                        style={{ objectFit: cover.fit }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="pa-serif text-base md:text-lg" style={{ color: "var(--pa-text-primary)" }}>{it.title}</h3>
                      {it.locked ? (
                        <span className="pa-badge-locked"><Lock size={10} /> Locked</span>
                      ) : (
                        <span className="pa-badge-owned"><Check size={10} /> Owned</span>
                      )}
                    </div>
                    <p className="text-xs md:text-sm line-clamp-2" style={{ color: "var(--pa-text-secondary)" }}>{it.summary}</p>
                    {it.locked && it.price_cents > 0 && (
                      <p className="text-xs mt-1" style={{ color: "var(--pa-gold)" }}>${(it.price_cents / 100).toFixed(2)}</p>
                    )}
                  </div>
                  <ArrowRight size={16} color="var(--pa-gold)" />
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {bundle && !bundle.owned && (
        <section
          className="pa-card p-5 md:p-6"
          style={{ borderColor: "var(--pa-gold-dark)" }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--pa-gold)" }}>Complete bundle</p>
              <h3 className="pa-serif text-xl mb-1" style={{ color: "var(--pa-text-primary)" }}>Unlock everything</h3>
              <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>
                Quiz, Starter Kit, Guidebook, and Manual. Best value.
              </p>
            </div>
            <button
              onClick={openWixStore}
              className="pa-btn-gold"
              data-testid="library-bundle-cta"
            >
              ${(bundle.price_cents / 100).toFixed(2)} · Get bundle
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
